import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Crown, ChevronDown, Check, Search, Network } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/contexts/OrgContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useIsAssociationAdmin } from "@/hooks/useAssociation";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const useAllOrgsForSwitcher = (enabled: boolean) =>
  useQuery({
    queryKey: ["org_switcher_orgs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, primary_color, logo_square_url, association_id")
        .order("name");
      if (error) throw error;
      return data || [];
    },
    enabled,
    staleTime: 60_000,
  });

const OrgSwitcher = () => {
  const { isMasterAdmin, isAssociationAdmin } = useIsAdmin();
  const { data: assocData } = useIsAssociationAdmin();
  const { orgId, overrideOrgName, overrideOrgLogo, setOrgOverride, clearOrgOverride } = useOrg();
  const canSwitch = isMasterAdmin || isAssociationAdmin;
  const { data: orgs } = useAllOrgsForSwitcher(!!canSwitch);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // For association admins, filter to only their association's orgs
  const availableOrgs = useMemo(() => {
    if (!orgs) return [];
    if (isMasterAdmin) return orgs;
    if (isAssociationAdmin && assocData?.associationIds?.length) {
      return orgs.filter((o: any) => assocData.associationIds.includes(o.association_id));
    }
    return [];
  }, [orgs, isMasterAdmin, isAssociationAdmin, assocData]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return availableOrgs;
    return availableOrgs.filter((o: any) => o.name.toLowerCase().includes(q));
  }, [availableOrgs, search]);

  if (!canSwitch) return null;

  const homeLabel = isMasterAdmin ? "Platform Admin" : "Association Admin";
  const HomeIcon = isMasterAdmin ? Crown : Network;
  const currentLabel = overrideOrgName || homeLabel;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all hover:bg-secondary border border-border/50 max-w-[200px]"
      >
        {overrideOrgName ? (
          overrideOrgLogo ? (
            <img src={overrideOrgLogo} alt="" className="w-4 h-4 rounded object-cover shrink-0" />
          ) : (
            <Building2 className="w-3.5 h-3.5 text-primary shrink-0" />
          )
        ) : (
          <HomeIcon className="w-3.5 h-3.5 text-warning shrink-0" />
        )}
        <span className="truncate text-foreground">{currentLabel}</span>
        <ChevronDown
          className={`w-3 h-3 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-secondary/50">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organizations..."
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>

          {/* Home option — always on top */}
          <div className="border-b border-border">
            <button
              type="button"
              onClick={() => {
                clearOrgOverride();
                setOpen(false);
                setSearch("");
                toast.success(`Returned to ${homeLabel}`);
                navigate(isMasterAdmin ? "/master-admin" : "/association");
              }}
              className={`flex items-center gap-2.5 w-full px-3 py-2.5 text-left hover:bg-secondary/60 transition-colors ${
                !overrideOrgName ? "bg-warning/8" : ""
              }`}
            >
              <HomeIcon className="w-3.5 h-3.5 text-warning shrink-0" />
              <span className="text-xs font-semibold text-foreground flex-1">{homeLabel}</span>
              {!overrideOrgName && <Check className="w-3.5 h-3.5 text-warning shrink-0" />}
            </button>
          </div>

          {/* Org list */}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">No organizations found</div>
            ) : (
              filtered.map((org: any) => {
                const isActive = overrideOrgName && orgId === org.id;
                return (
                  <button
                    type="button"
                    key={org.id}
                    onClick={() => {
                      setOrgOverride(org.id, org.name, org.logo_square_url);
                      setOpen(false);
                      setSearch("");
                      toast.success(`Switched to ${org.name}`);
                      navigate("/dashboard");
                    }}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-secondary/60 transition-colors border-b border-border/20 last:border-0 ${
                      isActive ? "bg-primary/5" : ""
                    }`}
                  >
                    {org.logo_square_url ? (
                      <img src={org.logo_square_url} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
                    ) : (
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold text-primary-foreground shrink-0"
                        style={{ backgroundColor: org.primary_color || "hsl(var(--primary))" }}
                      >
                        {org.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <span className="text-xs font-medium text-foreground flex-1 truncate">{org.name}</span>
                    {isActive && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
